import ordersApi from "@/api/orders.api";
import terminalsApi from "@/api/terminals.api";
import { CompanyInterface } from "@/data/interfaces/CompanyInterface";
import { Order } from "@/data/interfaces/OrderInterface";
import { ParamsData } from "@/data/interfaces/ParamsDataInterface";
import { Terminal } from "@/data/interfaces/TerminalInterface";
import {
  DIRECTION_DELIVERY,
  DIRECTION_PICKUP,
  DRIVER,
  TOUR,
  DONE,
} from "@/utils/constants";
import { getIdentifierNumber, getPrefixString } from "@/utils/methods";
// import mockTaskData from "@/utils/mockData/task-orders";
// import mockTourData from "@/utils/mockData/tour-orders";
// import mockDoneData from "@/utils/mockData/done-orders";
import { i18n } from "@/i18n/i18n";
import { RootState } from "../types";

interface OrdersByTimeAdded {
  [containerId: string]: Date;
}

// Define a type for dispatch actions
type Dispatch = (
  action: string,
  payload?: any,
  options?: { root: boolean }
) => Promise<any>; // Adjust the return type as per your actions

interface OrderCreateParams {
  terminalId: string;
  terminalCountryCode: number;
  orderReference: string;
  directionType: string;
  freightForwarderId?: string;
  operatorId?: string;
}

export interface OrderState {
  orders: Order[];
  terminals: Terminal[];
  tours: Order[];
  done: Order[];
  slotLanes: string[];
  containerPickupIds: Set<string>; // Allows us to pick first item from a returned array that all have the same direction, terminal, and ref
  containerDeliveryIds: Set<string>; // Allows us to pick first item from a returned array that all have the same direction, terminal, and ref
  ordersByTimeAdded: OrdersByTimeAdded; // allows us to keep the orders list in the same order
}

const OrderStore = {
  namespaced: true,
  state: {
    orders: [],
    terminals: [],
    tours: [],
    done: [],
    slotLanes: ["1/A", "3/A", "4/A", "2/B", "3/B", "4/B", "1/C", "3/C", "4/C"],
    containerPickupIds: new Set<string>(),
    containerDeliveryIds: new Set<string>(),
    ordersByTimeAdded: localStorage.getItem("ordersByTimeAdded")
      ? JSON.parse(localStorage.getItem("ordersByTimeAdded")!)
      : {},
  } as OrderState,
  mutations: {
    setOrders(state: OrderState, data: any[]) {
      const orderData: Order[] = data
        .filter(
          (item) =>
            item.roadTransportAssignmentStatus &&
            item.roadTransportAssignmentStatus == "DRIVER"
        )
        .map((item) => {
          item["loading_unit_formatted"] = "";
          const loadUnit = item.loadUnit as Order["loadUnit"];

          let { identifierPrefix, identifierNumber } = loadUnit;
          if (identifierPrefix || identifierNumber) {
            if (typeof identifierPrefix == "undefined") {
              identifierPrefix = "";
            }
            if (typeof identifierNumber == "undefined") {
              identifierNumber = "";
            }
            item.loading_unit_formatted = `${identifierPrefix}${identifierNumber}`;
            if (item.directionType == DIRECTION_DELIVERY) {
              state.containerDeliveryIds.add(
                `${identifierPrefix}${identifierNumber}`
              );
            } else {
              state.containerPickupIds.add(
                `${identifierPrefix}${identifierNumber}`
              );
            }
            if (
              !state.ordersByTimeAdded[`${identifierPrefix}${identifierNumber}`]
            ) {
              state.ordersByTimeAdded[
                `${identifierPrefix}${identifierNumber}`
              ] = new Date();
              // update the whole localstorage
              localStorage.setItem(
                "ordersByTimeAdded",
                JSON.stringify(state.ordersByTimeAdded)
              );
            }
          }
          return item;
        });
      orderData.sort((orderA, orderB) => {
        const d1 = state.ordersByTimeAdded[orderA.loading_unit_formatted]!;
        const d2 = state.ordersByTimeAdded[orderB.loading_unit_formatted]!;
        return compareDates(d1, d2);
      });
      state.orders = orderData;
      // if (process.env.NODE_ENV === "development") {
      //   // TODO comment out
      //   state.orders = [...mockTaskData, ...state.orders];
      // }
    },
    setTours(state: OrderState, data: any[]) {
      state.tours = data
        .filter((item) => item.roadTransportAssignmentStatus == "TOUR")
        .map((item, index) => {
          item["loading_unit_formatted"] = "";
          const loadUnit = item.loadUnit as Order["loadUnit"];

          let { identifierPrefix, identifierNumber } = loadUnit;
          if (identifierPrefix || identifierNumber) {
            if (typeof identifierPrefix == "undefined") {
              identifierPrefix = "";
            }
            if (typeof identifierNumber == "undefined") {
              identifierNumber = "";
            }
            item.loading_unit_formatted = `${identifierPrefix}${identifierNumber}`;
          }

          item.slotLane = state.slotLanes[index];

          return item;
        });
      // if (process.env.NODE_ENV === "development") {
      //   state.tours = [...mockTourData, ...state.tours];
      // }
    },
    setDone(state: OrderState, data: any[]) {
      state.done = data
        .filter((item) => item.roadTransportAssignmentStatus == "DONE")
        .map((item) => {
          item["loading_unit_formatted"] = "";
          const loadUnit = item.loadUnit as Order["loadUnit"];

          let { identifierPrefix, identifierNumber } = loadUnit;
          if (identifierPrefix || identifierNumber) {
            if (typeof identifierPrefix == "undefined") {
              identifierPrefix = "";
            }
            if (typeof identifierNumber == "undefined") {
              identifierNumber = "";
            }
            item.loading_unit_formatted = `${identifierPrefix}${identifierNumber}`;
          }

          return item;
        });
      // if (process.env.NODE_ENV === "development") {
      //   state.done = [...mockDoneData, ...state.done];
      // }
    },
    setTerminals(state: OrderState, data: any[]) {
      state.terminals = data;
    },
    addOrder(state: OrderState, data: Order) {
      state.orders.push(data);
      if (data.directionType == DIRECTION_DELIVERY) {
        state.containerDeliveryIds.add(
          `${data.loadUnit.identifierPrefix}${data.loadUnit.identifierNumber}`
        );
      } else {
        state.containerPickupIds.add(
          `${data.loadUnit.identifierPrefix}${data.loadUnit.identifierNumber}`
        );
      }
    },
    updateOrder(state: OrderState, data: { data: Order; key: number }) {
      const loadUnit = data.data.loadUnit as Order["loadUnit"];

      let { identifierPrefix, identifierNumber } = loadUnit;
      if (identifierPrefix || identifierNumber) {
        if (typeof identifierPrefix == "undefined") {
          identifierPrefix = "";
        }
        if (typeof identifierNumber == "undefined") {
          identifierNumber = "";
        }
        data.data.loading_unit_formatted = `${identifierPrefix}${identifierNumber}`;
      }
      // update containerIds set
      const orderToUpdate = state.orders[data.key];
      const oldLoadingUnitFormatted = orderToUpdate.loading_unit_formatted;
      if (orderToUpdate.directionType == DIRECTION_DELIVERY) {
        state.containerDeliveryIds.delete(oldLoadingUnitFormatted);
        state.containerDeliveryIds.add(data.data.loading_unit_formatted);
      } else {
        state.containerPickupIds.delete(oldLoadingUnitFormatted);
        state.containerPickupIds.add(data.data.loading_unit_formatted);
      }
      // update ordersByTimeAdded object in state and localStorage
      delete state.ordersByTimeAdded[oldLoadingUnitFormatted]; // remove old item with outdated loading unit formatted
      state.ordersByTimeAdded[data.data.loading_unit_formatted] = new Date(); // moves edited order to top of list
      localStorage.setItem(
        "ordersByTimeAdded",
        JSON.stringify(state.ordersByTimeAdded)
      );

      state.orders[data.key as number] = data.data;
    },
    addTourOrder(state: OrderState, data: { data: Order; key: number }) {
      state.tours.push(data.data);
      state.orders.splice(data.key, 1);
    },
    removeTourOrder(state: OrderState, data: { data: Order; key: number }) {
      state.orders.push(data.data);
      state.tours.splice(data.key, 1);
    },
    // removeOrder(state: OrderState, data: { data: Order; key: number }) {
    //   const indexTour = state.tours.findIndex(
    //     (tour) =>
    //       tour.orderId == data.data.orderId &&
    //       tour.orderPosition == data.data.orderPosition &&
    //       tour.directionType == data.data.directionType
    //   );

    //   const indexOrder = state.orders.findIndex(
    //     (order) =>
    //       order.orderId == data.data.orderId &&
    //       order.orderPosition == data.data.orderPosition &&
    //       order.directionType == data.data.directionType
    //   );
    //   if (indexTour !== -1) state.tours.splice(indexTour, 1);
    //   if (indexOrder !== -1) state.orders.splice(indexOrder, 1);
    // },
    requestCancelOrder(state: OrderState, data: { data: Order; key: number }) {
      // remove corresponding containerIds set
      const orderToCancel = state.orders[data.key as number];
      const loadingUnitFormatted = orderToCancel?.loading_unit_formatted ?? "";
      if (data.data.directionType == DIRECTION_DELIVERY) {
        state.containerDeliveryIds.delete(loadingUnitFormatted);
      } else {
        state.containerPickupIds.delete(loadingUnitFormatted);
      }

      // remove object in state and localStorage
      delete state.ordersByTimeAdded[loadingUnitFormatted]; // remove old item with outdated loading unit formatted

      if (data.data.roadTransportAssignmentStatus == "UNASSIGNED") {
        state.orders.splice(data.key, 1);
      } else {
        state.orders[data.key as number] = data.data;
      }
    },
    updateTourOrder(state: OrderState, data: { data: Order; key: number }) {
      const loadUnit = data.data.loadUnit as Order["loadUnit"];

      let { identifierPrefix, identifierNumber } = loadUnit;
      if (identifierPrefix || identifierNumber) {
        if (typeof identifierPrefix == "undefined") {
          identifierPrefix = "";
        }
        if (typeof identifierNumber == "undefined") {
          identifierNumber = "";
        }
        data.data.loading_unit_formatted = `${identifierPrefix}${identifierNumber}`;
      }

      const index = state.tours.findIndex(
        (tour) =>
          tour.orderId == data.data.orderId &&
          tour.orderPosition == data.data.orderPosition &&
          tour.directionType == data.data.directionType
      );
      const indexOrder = state.orders.findIndex(
        (tour) =>
          tour.orderId == data.data.orderId &&
          tour.orderPosition == data.data.orderPosition &&
          tour.directionType == data.data.directionType
      );
      if (data.data.roadTransportAssignmentStatus == "DONE") {
        state.tours.splice(index, 1);
        state.orders.splice(indexOrder, 1);
      } else {
        state.tours[index] = data.data;
      }
    },
  },
  getters: {
    getOrder:
      (state: OrderState) =>
      (
        id: string,
        position: number,
        direction: typeof DIRECTION_DELIVERY | typeof DIRECTION_PICKUP
      ) => {
        const order = state.orders.find(
          (order) =>
            order.orderId == id &&
            order.orderPosition == position &&
            order.directionType == direction
        );
        return order;
      },
  },
  actions: {
    async getOrders({
      commit,
      rootState,
      dispatch,
    }: {
      commit: (mutation: string, payload: any) => void;
      rootState: RootState["state"];
      dispatch: Dispatch;
    }) {
      const params = {
        driverId: rootState.user?.data?.user_id,
        roadTransportAssignmentStatus: DRIVER,
      };
      const response = await ordersApi.orders(params).catch((error) => {
        dispatch(
          "ErrorModalStore/toggleErrorModal",
          {
            errorType: "updateOrderError",
          },
          { root: true }
        );
      });

      commit("setOrders", response);
    },
    async getDone({
      commit,
      rootState,
      dispatch,
    }: {
      commit: (mutation: string, payload: any) => void;
      rootState: RootState["state"];
      dispatch: Dispatch;
    }) {
      const params = {
        driverId: rootState.user?.data?.user_id,
        roadTransportAssignmentStatus: DONE,
      };
      const response = await ordersApi.orders(params).catch((error) => {
        dispatch(
          "ErrorModalStore/toggleErrorModal",
          {
            errorType: "updateOrderError",
          },
          { root: true }
        );
      });

      commit("setDone", response);
    },
    async updateOrder(
      {
        commit,
        rootState,
        dispatch,
      }: {
        commit: (mutation: string, payload: any) => void;
        rootState: RootState["state"];
        dispatch: Dispatch;
      },
      data: { order: Order; index: number; params: ParamsData }
    ) {
      data.order.loadUnit.identifierPrefix = getPrefixString(
        data.order.loading_unit_formatted
      );
      data.order.loadUnit.identifierNumber = getIdentifierNumber(
        data.order.loading_unit_formatted
      );

      data.params = {
        ...data.params,
        freightForwarderName: rootState.user?.data?.user_id
          ? rootState.user?.data?.company?.name
          : "",
        freightForwarderId: rootState.user?.data?.user_id
          ? rootState.user?.data?.company_id
          : "",
      };
      try {
        if (Object.keys(data.params).length > 0) {
          const response = await ordersApi
            .edit(data.order, data.params)
            .catch((error) => {
              dispatch(
                "ErrorModalStore/toggleErrorModal",
                {
                  errorType: "updateOrderError",
                },
                { root: true }
              );
            });

          commit("updateOrder", { data: response, key: data.index });
        }
      } catch (error: any) {
        if (error.response.data.httpMessage === "CONFLICT") {
          throw new Error("error parallel edits");
        }
        console.error("Failed to update order:", error);
      }
    },
    async getTours({
      commit,
      rootState,
      dispatch,
    }: {
      commit: (mutation: string, payload: any) => void;
      rootState: RootState["state"];
      dispatch: Dispatch;
    }) {
      const params = {
        driverId: rootState.user?.data?.user_id,
        roadTransportAssignmentStatus: TOUR,
      };
      const response = await ordersApi.orders(params).catch((error) => {
        dispatch(
          "ErrorModalStore/toggleErrorModal",
          {
            errorType: "updateOrderError",
          },
          { root: true }
        );
      });

      commit("setTours", response);
    },
    async addTourOrder(
      {
        commit,
        rootState,
        dispatch,
      }: {
        commit: (mutation: string, payload: any) => void;
        rootState: RootState["state"];
        dispatch: Dispatch;
      },
      data: { order: Order; index: number; params: ParamsData }
    ) {
      data.order.roadTransportAssignmentStatus = "TOUR";
      const updatedLoadUnit = updateLoadUnitFormatted(data.order, data.params);
      data.order.loading_unit_formatted =
        updatedLoadUnit.loading_unit_formatted;
      data.order.loadUnit = {
        ...data.order.loadUnit,
        ...updatedLoadUnit.loadUnit,
      };
      try {
        const response = await ordersApi
          .edit(data.order, {
            ...data.params,
            roadTransportAssignmentStatus: "TOUR",
            licensePlate: rootState.user?.data?.license_plate,
          })
          .catch((error) => {
            dispatch(
              "ErrorModalStore/toggleErrorModal",
              {
                errorType: "updateOrderError",
              },
              { root: true }
            );
          });

        commit("updateOrder", {
          data: response,
          key: data.index,
        });

        commit("addTourOrder", {
          data: response,
          key: data.index,
        });
      } catch (error: any) {
        if (error.response.data.httpMessage === "CONFLICT") {
          throw new Error("error parallel edits");
        }
        console.error("Failed to update order:", error);
      }
    },
    async removeTour(
      {
        commit,
        dispatch,
        rootState,
      }: {
        commit: (mutation: string, payload: any) => void;
        dispatch: Dispatch;
        rootState: RootState["state"];
      },
      data: { order: Order; index: number }
    ) {
      data.order.roadTransportAssignmentStatus = "DRIVER";
      data.order.loadUnit.identifierPrefix = getPrefixString(
        data.order.loading_unit_formatted
      );
      data.order.loadUnit.identifierNumber = getIdentifierNumber(
        data.order.loading_unit_formatted
      );
      const response = await ordersApi
        .edit(data.order, {
          roadTransportAssignmentStatus: "DRIVER",
          driverId: rootState.user.data.user_id,
        } as ParamsData)
        .catch((error) => {
          dispatch(
            "ErrorModalStore/toggleErrorModal",
            {
              errorType: "updateOrderError",
            },
            { root: true }
          );
        });

      commit("removeTourOrder", { data: response, key: data.index });
    },
    async requestCancelOrder(
      {
        commit,
        dispatch,
      }: {
        commit: (mutation: string, payload: any) => void;
        dispatch: Dispatch;
      },
      data: { order: Order; index: number; params: ParamsData }
    ) {
      data.order.loadUnit.identifierPrefix = getPrefixString(
        data.order.loading_unit_formatted
      );
      data.order.loadUnit.identifierNumber = getIdentifierNumber(
        data.order.loading_unit_formatted
      );

      data.params = {
        ...data.params,
        freightForwarderName: "",
        freightForwarderId: "",
      };

      const response = await ordersApi
        .edit(data.order, data.params)
        .catch((error) => {
          dispatch(
            "ErrorModalStore/toggleErrorModal",
            {
              errorType: "updateOrderError",
            },
            { root: true }
          );
        });

      commit("requestCancelOrder", { data: response, key: data.index });
    },
    // async removeOrder(
    //   // ONLY FOR TEST PURPOSES
    //   {
    //     commit,
    //   }: {
    //     commit: (mutation: string, payload: any) => void;
    //   },
    //   data: { order: Order; index: number }
    // ) {
    //   await ordersApi.delete(data.order);

    //   commit("removeOrder", { data: data.order, key: data.index });
    // },
    async updateTourOrder(
      {
        commit,
        dispatch,
        rootState,
      }: {
        commit: (mutation: string, payload: any) => void;
        dispatch: Dispatch;
        rootState: RootState["state"];
      },
      data: { order: Order; params: ParamsData }
    ) {
      const updatedLoadUnit = updateLoadUnitFormatted(data.order, data.params);
      data.order.loading_unit_formatted =
        updatedLoadUnit.loading_unit_formatted;
      data.order.loadUnit = {
        ...data.order.loadUnit,
        ...updatedLoadUnit.loadUnit,
      };

      if (data.order.roadTransportAssignmentStatus == DRIVER) {
        data.order.roadTransportAssignmentStatus = TOUR;
      }

      data.params = {
        ...data.params,
        freightForwarderName: rootState.user?.data?.user_id
          ? rootState.user?.data?.company?.name
          : "",
        freightForwarderId: rootState.user?.data?.user_id
          ? rootState.user?.data?.company_id
          : "",
      };
      try {
        if (Object.keys(data.params).length > 0) {
          const response = await ordersApi
            .edit(data.order, data.params)
            .catch((error) => {
              dispatch(
                "ErrorModalStore/toggleErrorModal",
                {
                  errorType: "updateOrderError",
                },
                { root: true }
              );
            });

          commit("updateTourOrder", { data: response });
        }
      } catch (error: any) {
        if (error.response.data.httpMessage === "CONFLICT") {
          throw new Error("error parallel edits");
        }
        console.error("Failed to update tour order:", error);

        dispatch(
          "ErrorModalStore/toggleErrorModal",
          {
            errorType: "updateOrderError",
          },
          { root: true }
        );
      }
    },
    async getTerminals({
      commit,
    }: {
      commit: (mutation: string, payload: any) => void;
    }) {
      const response = await terminalsApi.terminals();

      commit("setTerminals", response);
    },
    async saveOrder(
      {
        commit,
        state,
        rootState,
        dispatch,
      }: {
        commit: (mutation: string, payload: any) => void;
        state: OrderState;
        rootState: RootState["state"];
        dispatch: Dispatch;
      },
      order: Order
    ) {
      try {
        let params = {
          terminalId: order.terminal.terminalId.locationCode,
          terminalCountryCode: order.terminal.terminalId.authority,
          orderReference: order.primaryReference,
          directionType: order.directionType,
        } as OrderCreateParams;
        if (rootState.user?.data?.company?.company_type == "Operator") {
          params = {
            ...params,
            operatorId: rootState.user?.data?.company.operator_id,
          };
        } else {
          params = {
            ...params,
            freightForwarderId: rootState.user?.data?.company_id,
          };
        }
        if (order.directionType === DIRECTION_PICKUP) {
          const loading_unit_formatted = {
            loadUnitIdPrefix: getPrefixString(order.loading_unit_formatted),
            loadUnitIdNumber: getIdentifierNumber(order.loading_unit_formatted),
          };
          params = { ...params, ...loading_unit_formatted };
        }
        const response = await ordersApi.createOrder(params).catch((error) => {
          dispatch(
            "ErrorModalStore/toggleErrorModal",
            {
              errorType: "updateOrderError",
            },
            { root: true }
          );
        });

        if (response.length) {
          // const orderAssignable = response[0]; // TODO - go back to this once we have containerIds provided by api
          const orderAssignable = response.find((order: Order) => {
            let { identifierPrefix, identifierNumber } = order.loadUnit;
            if (typeof identifierPrefix == "undefined") {
              identifierPrefix = "";
            }
            if (typeof identifierNumber == "undefined") {
              identifierNumber = "";
            }
            const orderContainerId = `${identifierPrefix}${identifierNumber}`;
            if (order.directionType == DIRECTION_DELIVERY) {
              return !state.containerDeliveryIds.has(orderContainerId);
            }

            return !state.containerPickupIds.has(orderContainerId);
          });
          if (orderAssignable) {
            (orderAssignable.driverId = rootState.user?.data?.user_id),
              (orderAssignable.roadTransportAssignmentStatus = "DRIVER");
            const orderData = await ordersApi.edit(orderAssignable, {
              driverId: rootState.user?.data?.user_id,
              roadTransportAssignmentStatus: "DRIVER",
              freightForwarderName: rootState.user?.data?.user_id
                ? rootState.user?.data?.company?.name
                : "",
              freightForwarderId: rootState.user?.data?.user_id
                ? rootState.user?.data?.company_id
                : "",
            });

            commit("addOrder", orderData);
          } else {
            const error = new Error("Duplicate order");
            error.message = "Order already exists in orders list";
            throw error;
          }
          return true;
        } else {
          // empty API array response
          const error = new Error("Not Found");
          error.message = "Order not found";
          throw error;
        }
      } catch (error) {
        console.log(error);
        console.error("Error saving order!");
        throw error;
      }
    },
  },
};

function compareDates(d1: Date, d2: Date) {
  const date1 = new Date(d1).getTime();
  const date2 = new Date(d2).getTime();

  if (date1 < date2) {
    return 1;
  } else if (date1 > date2) {
    return -1;
  } else {
    return 0;
  }
}

function updateLoadUnitFormatted(order: Order, params: any) {
  let prefixString = "";
  let identifierNumber = "";
  if (params.loading_unit_formatted) {
    prefixString = getPrefixString(params.loading_unit_formatted);
    identifierNumber = getIdentifierNumber(params.loading_unit_formatted);
  } else {
    prefixString = getPrefixString(order.loading_unit_formatted);
    identifierNumber = getIdentifierNumber(order.loading_unit_formatted);
  }

  return {
    loading_unit_formatted: `${prefixString}${identifierNumber}`,
    loadUnit: { prefixString, identifierNumber },
  };
}
export default OrderStore;
